import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateFileShareInput, FileShare } from '@jobos/shared';

import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';

// Client share links, office side. Ordinary React Query — a share is a small
// row, and nothing here touches bytes.

export const fileShareKeys = {
  all: ['fileShares'] as const,
  list: (jobId: string) => [...fileShareKeys.all, 'list', jobId] as const,
};

/**
 * The public URL for a token.
 *
 * Built from the *frontend* origin, not the API base: the link goes in an email
 * to a client, who lands on the gallery page, which then talks to the API.
 */
export function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

export function useFileShares(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: fileShareKeys.list(jobId!),
    queryFn: async () => {
      const res = await apiClient.get<{ shares: FileShare[] }>(`/jobs/${jobId}/shares`);
      return res.shares;
    },
    enabled: !!jobId && enabled,
  });
}

function useShareErrorToast(fallback: string) {
  const { toast } = useToast();
  return (error: Error) => {
    toast({
      title: 'Error',
      description: error.message || fallback,
      variant: 'destructive',
    });
  };
}

export function useCreateFileShare(jobId: string) {
  const queryClient = useQueryClient();
  const onError = useShareErrorToast('Failed to create the link.');

  return useMutation({
    mutationFn: async (input: CreateFileShareInput) => {
      const res = await apiClient.post<{ share: FileShare }>(`/jobs/${jobId}/shares`, input);
      return res.share;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileShareKeys.list(jobId) });
    },
    onError,
  });
}

export function useRevokeFileShare(jobId: string) {
  const queryClient = useQueryClient();
  const onError = useShareErrorToast('Failed to revoke the link.');

  return useMutation({
    mutationFn: async (shareId: string) => {
      await apiClient.delete(`/jobs/${jobId}/shares/${shareId}`);
      return shareId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileShareKeys.list(jobId) });
    },
    onError,
  });
}
